import { PublicResource, ResourceType } from './resource-types';
import { SearchResult } from './schema/resources/search-result';

export interface Registration extends PublicResource {
  email: string;
  full_name: string;
  token: string;
  password: string;
}

// #region Project types
export enum ProjectStatuses {
  'ProjectStatusOpen' = 'ProjectStatusOpen',
  'ProjectStatusArchived' = 'ProjectStatusArchived',
  'ProjectStatusDraft' = 'ProjectStatusDraft'
}
export type ProjectStatus = keyof typeof ProjectStatuses;

export const PROJECT_ROLE_TYPES = {
  Admin: 'ProjectMemberRoleAdmin' as const,
  Member: 'ProjectMemberRoleMember' as const
};
type Keys = keyof typeof PROJECT_ROLE_TYPES;
export type ProjectRoleType = (typeof PROJECT_ROLE_TYPES)[Keys];
// endregion

// #region Asset types
export type AssetVariant =
  | 'video'
  | 'image'
  | 'audio'
  | 'document'
  | 'compose'
  | 'pdf'
  | 'url';

export interface MediaEntry {
  url: string;
  size: string;
  type: MediaTypes;
  width?: number;
  height?: number;
  page?: number;
  duration?: number;
}

export const assetProcessingStates = {
  FAILED: 'AssetProcessingStatusFailed',
  COMPLETE: 'AssetProcessingStatusComplete',
  PENDING: 'AssetProcessingStatusPending',
  PROCESSING: 'AssetProcessingStatusProcessing',
  SCANNING: 'AssetProcessingStatusScanning',
  COMPOSING: 'AssetProcessingStatusComposing',
  UPLOADING: 'AssetProcessingStatusUploading',
  UNKNOW: 'AssetProcessingStatusUnknown',
  //added parts
  PAUSED: 'AssetUploadStatusPaused',
  UPLOAD_PENDING: 'AssetUploadStatusPending',
  UPLOAD_FAILED: 'AssetUploadStatusFailed',
  UPLOAD_INITIALIZATIONFAILED: 'AssetUploadStatusUplodadInitFailed',
  UPLOAD_MISSING: 'AssetUploadStatusUplodadMissing'
} as const;

export type AssetProcessingStates =
  (typeof assetProcessingStates)[keyof typeof assetProcessingStates];

export const mediaTypes = {
  PREVIEW: 'preview',
  THUMBNAIL: 'thumbnail',
  POSTER: 'poster',
  SOURCE: 'source',
  STRIP: 'strip'
};

export type MediaTypes = (typeof mediaTypes)[keyof typeof mediaTypes];

export type AssetMedia = MediaEntry[];

export const assetMimeTypes = {
  UNKNOWN: 'application/octet-stream',
  FOLDER: 'application/vnd.cube.folder',
  DOCUMENT: 'application/vnd.cube.document',
  DOCUMENT_PDF: 'application/vnd.cube.document.pdf',
  AUDIO: 'application/vnd.cube.audio',
  IMAGE: 'application/vnd.cube.image',
  VIDEO: 'application/vnd.cube.video',
  COMPOSE: 'application/vnd.cube.compose',
  ARCHIVE: 'application/vnd.cube.archive',
  PROJECT: 'application/vnd.cube.project',
  URL: 'application/vnd.cube.url',
  BANNER: 'application/vnd.cube.banner',
  // FILREQUEST: 'application/vnd.cube.share.request'
} as const;

export type AssetMimeTypes =
  (typeof assetMimeTypes)[keyof typeof assetMimeTypes];

// #endregion

export interface RequestError {
  code?: string;
  detail?: string;
  status?: string;
  title?: string;
  source?: {};
}

// #region export
export type IntegrationType = 'global' | 'workspace' | 'user' | 'account';
export type VideoType = typeof assetMimeTypes.AUDIO;

/**
 * YouTube
 */

export type YoutubeChannelType = {
  id: string;
  display_name: string;
  display_image?: string;
};

interface YoutubeCategory {
  display_name: string;
  id: string;
}

export interface YoutubePlaylist extends YoutubeCategory { }
export interface YouTubeConfig {
  channels: YoutubeChannelType[];
  categories?: YoutubeCategory[];
  playlists?: YoutubePlaylist[];
}

export interface YouTubeParameters {
  title: string;
  description: string;
  channel: string;
  channel_name: string;
  playlists: string[];
  category_id: string;
  visibility: string;
  tags: string[];
}

/**
 * Meta
 */
export interface MetaAdAccount {
  display_name: string;
  id: string;
}

export interface MetaConfig {
  adAccounts: MetaAdAccount[];
}

export interface MetaParameters {
  name: string;
  adAccountID: string;
}

/**
 * CM360
 */

export interface CM360Advertiser {
  display_name: string;
  id: string;
}

export interface CM360Profile {
  display_name: string;
  id: string;
  advertisers: CM360Advertiser[];
}

export interface CM360Config {
  profiles: CM360Profile[];
}

export interface CM360Parameters {
  display_name: string;
  advertiser_id: string;
  profile_id: string;
}

/**
 * GoogleAds
 */

export interface GoogleAdsCustomer {
  display_name: string;
  id: string;
}

export interface GoogleAdsConfig {
  customers: GoogleAdsCustomer[];
}

export interface GoogleAdsParameters {
  display_name: string;
  customer_id: string;
}

/**
 * Contentful
 */
export interface ContentfulEnvironment {
  display_name: string;
  id: string;
}

export interface ContentfulSpace {
  display_name: string;
  id: string;
  environments: ContentfulEnvironment[];
}

export interface ContentfulConfig {
  spaces: ContentfulSpace[];
}

export interface ContentfulParameters {
  display_name: string;
  space: string;
  environment: string;
}

export interface TikTokParameters {
  caption: string;
  privacy_level: string;
  post_directly: boolean;
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
}

interface RadioParameters {
  title: string;
  commercial_length: number;
  advertiser: string;
  production_company: string;
  stations: string[];
  first_broadcast_date: string;
  broadcaster_instructions?: string;
  advertising_agency?: string;
  media_agency?: string;
}
interface ExportsData {
  radio: { medialab: RadioParameters };
  broadcast: { broadcast: BroadcastParameters };
}

export interface CreateExportsData<T extends keyof ExportsData = 'broadcast'> {
  parameters: ExportsData[T];
  asset_id: string;
  target_id: string;
}

export interface TikTokCreatorInfo {
  display_name: string;
  id: string;
  privacy_level_options: string[];
  duet_disabled: boolean;
  comment_disabled: boolean;
  stich_disabled: boolean;
  max_video_post_duration: number;
}
export interface TikTokConfig {
  creator_info: TikTokCreatorInfo[];
}

/**
 * Broadcast
 */

type CommercialType = 'Commercial' | 'Billboard' | 'Promo';

interface BroadcastParameters {
  title: string;
  product_service: string;
  advertiser: string;
  commercial_length: number;
  version: number;
  export_filename?: string;
  tagon?: boolean;
  omruil?: boolean;
  low_loudness?: boolean;
  commercial_type?: CommercialType;
}
export type BaseIntegrationParams = {};

export type BaseIntegrationConfig = {};

export interface ExportTargetImplementations {
  youtube: {
    intergration_parameters: YouTubeParameters;
    integration_config: YouTubeConfig;
  };
  meta: {
    intergration_parameters: MetaParameters;
    integration_config: MetaConfig;
  };
  cm360: {
    intergration_parameters: CM360Parameters;
    integration_config: CM360Config;
  };
  googleads: {
    intergration_parameters: GoogleAdsParameters;
    integration_config: GoogleAdsConfig;
  };
  contentful: {
    intergration_parameters: ContentfulParameters;
    integration_config: ContentfulConfig;
  };
  tiktok: {
    intergration_parameters: TikTokParameters;
    integration_config: TikTokConfig;
  };
  default: {
    intergration_parameters: BaseIntegrationParams;
    integration_config: BaseIntegrationConfig;
  };
  broadcast: {
    intergration_parameters: BroadcastParameters;
    integration_config: BaseIntegrationConfig;
  };
}
// #endregion

// #region library types

export interface ToolInterface {
  label: string;
  handler?: Function;
  icon: string;
  tip?: string;
  disabled?: boolean;
  orderDirection?: string;
  field: string;
  fieldParent?: string;
}

export interface FilterInterface {
  id: string;
  label: string;
  fn: Function;
  icon?: React.ReactNode;
  tip?: string;
}

/** Library column type in list view */
export interface ColumnInterface {
  field: string;
  label: string;
  sort?: Function;
  icon?: string;
  groupBy?: Function;
  component?: Function;
  handler?: Function;
  disabled?: boolean;
  tools?: ToolInterface[];
  hover?: string;
  styles?: { width: string; padding: string };
  view?: string;
  colListview?: number;
  toolTip?: Function;
  numeric?: boolean;
  showLabel?: boolean;
  listType?: string;
}
interface LinkData {
  from?: string;
  to: string;
  state?: {
    siblings: LibraryItem[];
    from: string;
  };
}

/** library item */
export type ContextVariant =
  | 'library'
  | 'trash'
  | 'search'
  | 'sharelinkView'
  | 'projects'
  | 'favorites'
  | 'sharelinkViewNoDates'
  | 'projectTemplates';
export type ContentTreeContext = ContextVariant | 'figma';

type ItemType = {
  'library-item': LibraryItem;
  'search-result': SearchResult;
};

export type SortableListType =
  | 'thumbNail'
  | 'project_status_access'
  | 'switch_view'
  | 'display_name'
  | 'updated_at'
  | 'created_at'
  | 'deleted_at'
  | 'duration'
  | 'file_size'
  | 'removed_date'
  | 'type'
  | 'members'
  | 'search_results'
  | 'tags';

export interface ContentTreeItemProperties<
  T extends keyof ItemType = 'library-item'
> {
  libraryItem: ItemType[T];
  isCut?: boolean;
  gridView: boolean;
  columns: ColumnInterface[];
  colListview?: number;
  onContextMenu: () => void;
  onActivateContextualClick: () => void;
  /** disabled in trash bin */
  navigateToItem?: (ev?) => void;
  variant?: ContentTreeContext;
  renderContextMenu?: () => JSX.Element;
  sortBy?: SortableListType;
}
export interface LibraryItem {
  created_at?: string;
  display_name?: string;
  display_image?: string;
  updated_at?: string;
  deleted_at?: string;
  trashed_at?: string;
  type?: string;
  resource_id?: string;
  resource_type?: ResourceType;
  path?: string;
  id?: string;
  labels?: Array<string>;
  src?: string;
  link?: LinkData;
  file_size?: string;
  file_name?: string;
  context_resource?: {
    type: 'project' | 'library' | 'workspace'; // NOTE: we dont support search for workspace yet
    id: string;
  };

  /** project members */
  members?: any[];
  isProjectTemplate?: boolean;
  node_id?: string;
  mime_type?: AssetMimeTypes;
  // project
  archived?: boolean;
  is_public?: boolean;
  // asset
  strip_image?: string;
  processing_status?: AssetProcessingStates;
  media?: AssetMedia;
  /** `url` type assets */
  location?: string;
  extension?: string;
  // favorites
  favorited?: boolean;
  favoritedNodeIds?: string[];
  // project templates
  isDefaultTemplate?: boolean;
}
// #endregion
